import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant. Defaults to `primary`. */
  variant?: ButtonVariant;
  /** Size token. Defaults to `md`. */
  size?: ButtonSize;
  /** Show inline spinner and disable interaction. */
  loading?: boolean;
  /**
   * Render as the immediate child via Radix Slot. Useful to turn a Button
   * into a `<Link>`, `<a>`, etc. without losing the visual API.
   */
  asChild?: boolean;
  /** Optional content rendered before the children (icon slot). */
  leftIcon?: ReactNode;
  /** Optional content rendered after the children (icon slot). */
  rightIcon?: ReactNode;
}

const baseClass =
  'inline-flex items-center justify-center gap-2 rounded-card font-medium transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50 select-none';

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover active:bg-primary-hover/90',
  secondary:
    'bg-bg-card text-white border border-border hover:bg-bg-hover active:bg-bg-hover/80',
  ghost: 'bg-transparent text-white hover:bg-bg-hover/70 active:bg-bg-hover',
  danger: 'bg-danger text-white hover:bg-danger/90 active:bg-danger/80',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

/**
 * Primary button primitive.
 *
 * Implements four visual variants and three sizes (matches DESIGN seção 9).
 * When `loading` is true, a spinner replaces the optional left icon and the
 * button is automatically disabled.
 *
 * Use `asChild` together with a single child element to apply the styling to
 * an arbitrary element (e.g. `<Link>` from React Router).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    asChild = false,
    disabled,
    className,
    children,
    leftIcon,
    rightIcon,
    type,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const classes = cn(baseClass, variantClass[variant], sizeClass[size], className);

  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? <Spinner size={size === 'lg' ? 'md' : 'sm'} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
